import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { MapModelLike } from 'app/shared/models/map.model';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { MapsService } from 'app/shared/services/maps.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ObjectUtils } from 'app/shared/utils/object.utils';
import { NotesService } from '../../../../notes.service';
import { TranslationService } from '@services/translation.service';

interface MapFormLike {
  name: FormControl<string>;
  description: FormControl<string>;
}

@Component({
  selector: 'app-map-register',
  templateUrl: 'map-register.component.html',
  styleUrls: ['map-register.component.scss'],
})
export class MapRegisterComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Output()
  onSaved = new EventEmitter<any>();
  @Output()
  onClosed = new EventEmitter<any>();

  @Input()
  model: MapModelLike;

  formGroup: FormGroup<MapFormLike>;

  nameMaxLength = 50;
  descriptionMaxLength = 255;

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mapsService: MapsService,
    private mainMapService: MainMapService,
    private notesService: NotesService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private ts: TranslationService
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      name: this.formBuilder.control<string>(null, [
        Validators.required,
        Validators.maxLength(this.nameMaxLength),
      ]),
      description: this.formBuilder.control<string>(null, [
        Validators.required,
        Validators.maxLength(this.descriptionMaxLength),
      ]),
    });

    this.handleModel();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model) {
      this.handleModel();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onHidden() {
    this.onClosed.emit();
  }

  onBtnCancelClick() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onSubmit(e) {
    e?.preventDefault();
    if (this.loadingController.isShown()) {
      return;
    }

    let values: any = this.formGroup.getRawValue();
    values.basemapId = this.mainMapService.getBasemapId();
    values.viewPoint = this.mainMapService.getCurrentViewpoint();
    values.layers = this.mainMapService.getCurrentLayerVisibleList();
    values.notes = this.notesService.getNotes();

    let observable: Observable<any>;
    if (this.model?.id) {
      values = ObjectUtils.merge({}, this.model, values);
      observable = this.mapsService.updateMap(this.model.id, values);
    } else {
      observable = this.mapsService.createMap(values);
    }

    this.loadingController.show();
    observable
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['map-saved-successfully']
          );

          this.visible = false;
          this.visibleChange.emit(this.visible);

          this.onSaved.emit();
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-save-data']
          );
        },
      });
  }

  private handleModel() {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.reset(this.model ?? {});
  }
}
